import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { Product } from '~shared/models';
import { Observable, Subject } from 'rxjs';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { ListviewReporterService } from '~shared/modules/products-grid/services/listview-reporter.service';
import { PlpVariantRestyleService } from '~shared/services/plp-variant-restyle/plp-variant-restyle.service';
import { takeUntil } from 'rxjs/operators';
import { VariantType, VariantTypeObject } from '~shared/models/product-card/product-variant.model';
import { FOOD_CATEGORY, NUMBER_3, NUMBER_4 } from '~shared/constants';
import { Category } from '~app/modules/generic/models';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { AddToCardConfig } from '~shared/modules/product-card/models/add-to-card-config.model';
import { PlpAddToCartService } from '~shared/modules/products-grid/services/plp-add-to-cart.service';

@Component({
  selector: 'app-products-grid',
  templateUrl: './products-grid.component.html',
  styleUrls: ['./products-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductsGridComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() count: number;
  @Input() testId: string;
  @Input() products: Product[];
  @Input() isDesktop: boolean;
  @Input() isMobile: boolean;
  @Input() isCustomPLPPage: boolean;
  @Input() recommendation: boolean;
  @Input() page: number = 1;

  @Output() productClicked = new EventEmitter<{ product: Product; priceType: PriceTypes; index: number }>();
  @Output() productVisibilityChange = new EventEmitter<{ isVisible: boolean; index: number }>();
  @Output() indexPower10ProductVisible = new EventEmitter<number>();

  isBlackDealsEnabled$: Observable<boolean>;
  isPLP: boolean;
  isNewPLPVariantRestyle: boolean;
  public addToCartCategories: string[] = [FOOD_CATEGORY];
  public addToCartConfig: AddToCardConfig;
  private destroySubject$ = new Subject<boolean>();

  constructor(
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private listviewReporterService: ListviewReporterService,
    private plpVariantRestyleService: PlpVariantRestyleService,
    private optimizelyService: OptimizelyService,
    private plpAddToCartService: PlpAddToCartService
  ) {
    this.isBlackDealsEnabled$ = this.blackDealsFeatureFlagService.isEnabled();
  }

  ngOnInit() {
    const isFoodCategoryOrHasFoodItems: boolean = this.products?.some((product: Product) =>
      this.hasProductSomeAddToCartCategory(
        product?.categories.map((category: Category) => category?.id),
        [FOOD_CATEGORY]
      )
    );

    if (isFoodCategoryOrHasFoodItems) {
      sessionStorage.setItem('hasUserSeenFood', 'true');
      this.optimizelyService.initialize(true);
    }

    this.plpVariantRestyleService
      .isNewPLPVariantRestyle()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((isEnabled: boolean) => {
        this.isNewPLPVariantRestyle = isEnabled;
      });

    this.isPLP = this.plpVariantRestyleService.isPLP();

    this.plpAddToCartService
      .getPlpsAddToCartVariant()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((addToCartConfig: AddToCardConfig) => {
        this.addToCartConfig = addToCartConfig;
      });
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  trackById(_, product: Product): string {
    return product.id;
  }

  onProductClick(product: Product, priceType: PriceTypes, index: number): void {
    this.productClicked.emit({
      product,
      priceType,
      index,
    });
  }

  onProductVisibilityChange(isVisible: boolean, index: number): void {
    if (this.listviewReporterService.trackListView(index, this.page)) {
      this.indexPower10ProductVisible.emit(index);
    }

    this.productVisibilityChange.emit({
      isVisible,
      index,
    });
  }

  public someOtherRowProductHasVariants(index: number, isDesktop: boolean): { hasSizes: boolean; hasColors: boolean } {
    const restOfRowIndexes: number[] = this.getRowIndexesFromIndex(index, isDesktop);

    const hasSizes: boolean = restOfRowIndexes?.some((anotherIndex: number): boolean => {
      const variantTypeObject: VariantTypeObject = this.products[anotherIndex]?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const notColorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]) => variant[0] !== 'color'
      );
      return notColorVariants && !!notColorVariants?.length;
    });
    const hasColors: boolean = restOfRowIndexes?.some((anotherIndex: number): boolean => {
      const variantTypeObject: VariantTypeObject = this.products[anotherIndex]?.variant?.types;
      const variantTypes: [string, VariantType][] = variantTypeObject ? Object.entries(variantTypeObject) : [];
      const colorVariants: [string, VariantType][] = variantTypes?.filter(
        (variant: [string, VariantType]): boolean => variant[0] === 'color'
      );

      return colorVariants && !!colorVariants?.length;
    });

    const variantTypeObject: VariantTypeObject = this.products[index]?.variant?.types;
    const variantTypes: [string, VariantType][] = variantTypeObject
      ? Object.entries(this.products[index]?.variant?.types)
      : [];
    const currentProductNotColorVariants: [string, VariantType][] = variantTypes?.filter(
      (variant: [string, VariantType]) => variant[0] !== 'color'
    );
    const currentProductColorVariants: [string, VariantType][] = variantTypes?.filter(
      (variant: [string, VariantType]): boolean => variant[0] === 'color'
    );

    return {
      hasSizes: hasSizes || !!currentProductNotColorVariants?.length,
      hasColors: hasColors || !!currentProductColorVariants?.length,
    };
  }

  public someOtherRowProductHasAddToCart(index: number): boolean {
    const restOfRowIndexes: number[] = this.getRowIndexesFromIndex(index, this.isDesktop);
    return restOfRowIndexes?.some((anotherIndex: number): boolean => {
      const product: Product = this.products[anotherIndex];
      const productCategories: Category[] = product.categories;
      const allowedCategories: Set<string> = new Set(this.addToCartCategories);
      return productCategories?.some((category: Category) => allowedCategories.has(category?.id));
    });
  }

  rowHasTotalUnitsOrBasePrice(index: number, isDesktop: boolean) {
    const restOfRowIndexes: number[] = this.getRowIndexesFromIndex(index, isDesktop);
    return restOfRowIndexes?.some((index: number) => {
      return this.products[index]?.bestOffer?.unitPrice?.unitCount > 1 || this.products[index]?.bestOffer?.basePrice;
    });
  }

  private getRowIndexesFromIndex(index: number, isDesktop?: boolean): number[] {
    const restOfIndex: number[] = [];
    const columnCount: number = isDesktop ? NUMBER_4 : NUMBER_3;
    const row: number = Math.floor(index / columnCount);

    const firstRowIndex: number = row * columnCount;

    for (let i: number = firstRowIndex; i < firstRowIndex + columnCount; i++) {
      if (i !== index && i < this.products?.length) {
        restOfIndex.push(i);
      }
    }

    return restOfIndex;
  }

  private hasProductSomeAddToCartCategory(productCategories: string[], addToCartCategories: string[]): boolean {
    return productCategories.some((item) => addToCartCategories.includes(item));
  }
}
